import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { newApi } from '@/newApi';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { DynamicFilterGroup, FieldInfo } from '@sysarb/sysarb-types-v2/services/v1/analytics';
import { ConditionOperator, FilterOperator, FilterType } from '@sysarb/sysarb-types-v2/services/v1/analytics/enums/dynamicFilter';
import { DataSources, InputFieldNames } from '@sysarb/sysarb-types-v2/services/v1/analytics';
import type { EmploymentData, EmploymentFilterInput, EmploymentGroupFilterInput, EmploymentGrouping } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit/employment';
import type { EmploymentDataSelect, EmploymentDetail, EmploymentFilterSelectInput, WorkMatchAnalysisResult } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { UseQueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';

const restManager = new RestManager(`https://${getApiHost()}/pay-equity-audit-service`);

/**
 * Builds the audit's employment-work-details Resgate topic.
 * @param {string} payEquityAuditId - The audit.
 * @param {string | null | undefined} organisationNodeId - The user's organisation node.
 * @param {Role | null | undefined} role - The user's active role.
 * @returns {string} The topic.
 */
function employmentWorkDetailsTopic(payEquityAuditId: string, organisationNodeId: string | null | undefined, role: Role | null | undefined): string {
    const { instanceTopic } = useLoginStore();
    return `v1.${instanceTopic}.pay-equity-audit.${payEquityAuditId.toLowerCase()}.${organisationNodeId?.toLowerCase() ?? ''}.${role?.toString().toLowerCase() ?? ''}.employment-work-details`;
}

/**
 * The grid's employments endpoint, which varies by role.
 * @param {string} payEquityAuditId - The audit.
 * @param {string | null} organisationNodeId - The user's organisation node.
 * @param {Role | null} role - The user's active role.
 * @returns {string} Path below the service root, without the `v1/{instance}/` prefix.
 */
function employmentsPath(payEquityAuditId: string, organisationNodeId: string | null, role: Role | null): string {
    if (role === Role.OperationalAdministrator) return `pay-equity-audit/${payEquityAuditId}/organization/${organisationNodeId}/filtered-employment/operational-administrator`;
    if (role === Role.SalarySurveyAnalyst) return `pay-equity-audit/${payEquityAuditId}/organization/${organisationNodeId}/filtered-employment`;
    return `pay-equity-audit/${payEquityAuditId}/filtered-employment`;
}

/**
 * The grid's employment-groups endpoint, which varies by role.
 * @param {string} payEquityAuditId - The audit.
 * @param {string | null} organisationNodeId - The user's organisation node.
 * @param {Role | null} role - The user's active role.
 * @returns {string} Path below the service root, without the `v1/{instance}/` prefix.
 */
function employmentGroupsPath(payEquityAuditId: string, organisationNodeId: string | null, role: Role | null): string {
    if (role === Role.OperationalAdministrator) return `pay-equity-audit/${payEquityAuditId}/organization/${organisationNodeId}/filtered-employment-groups/operational-administrator`;
    if (role === Role.SalarySurveyAnalyst) return `pay-equity-audit/${payEquityAuditId}/organization/${organisationNodeId}/filtered-employment-groups`;
    return `pay-equity-audit/${payEquityAuditId}/filtered-employment-groups`;
}

/** Employments with no salary, or a salary of zero, in this audit. */
function zeroSalaryFilter(payEquityAuditId: string): DynamicFilterGroup {
    return {
        filterType: FilterType.filtergroup,
        operator: FilterOperator.and,
        filters: [
            {
                filterType: FilterType.filter,
                field: InputFieldNames.mappingId,
                conditionOperator: ConditionOperator.equalTo,
                value: payEquityAuditId
            },
            {
                filterType: FilterType.filtergroup,
                operator: FilterOperator.or,
                filters: [
                    {
                        filterType: FilterType.filter,
                        field: InputFieldNames.salary,
                        conditionOperator: ConditionOperator.equalTo,
                        value: 0
                    },
                    {
                        filterType: FilterType.filter,
                        field: InputFieldNames.salary,
                        conditionOperator: ConditionOperator.isNull,
                        value: null
                    }
                ]
            },
        ]
    };
}

export const payEquityAuditEmployments = {
    // NOTE on `enabled`: these factories take plain values, so `enabled: () => <flag>` returns a
    // constant captured when the factory ran — it does not make the query reactive on its own.
    // Reactivity comes from the caller wrapping the call in a getter, e.g.
    // `useQuery(() => api.payEquityAudit.employments.queries.getX(id.value))`, which re-runs the
    // factory when its inputs change. Passing the options object directly would freeze `enabled`.
    queries: {
        /**
         * Employments that exactly match the current selection.
         * @param {string | null | undefined} payEquityAuditId - The audit.
         * @param {string[]} employmentIds - The current selection.
         * @returns {UseQueryOptions<EmploymentDetail[], Error>} Query options.
         */
        getExactMatchingEmployments: (payEquityAuditId: string | null | undefined, employmentIds: string[]): UseQueryOptions<EmploymentDetail[], Error> => {
            const { instance } = useLoginStore();
            const hasInput = payEquityAuditId != null && payEquityAuditId !== '' && employmentIds.length > 0;

            return queryOptions<EmploymentDetail[], Error>({
                queryKey: ['payEquityAuditEmployments', 'exactMatchingEmployments', instance, payEquityAuditId, employmentIds],
                enabled: () => hasInput,
                queryFn: async (): Promise<EmploymentDetail[]> => {
                    if (payEquityAuditId == null || payEquityAuditId === '' || employmentIds.length === 0) return [];
                    return newApi.PayEquityAuditService.getExactMatchingEmployments(payEquityAuditId, employmentIds);
                },
            });
        },
        /**
         * Work-match suggestions for the current selection.
         * @param {string | null | undefined} payEquityAuditId - The audit.
         * @param {string[]} employmentIds - The current selection.
         * @param {string | null | undefined} organisationNodeId - The user's organisation node.
         * @param {Role | null | undefined} role - The user's active role.
         * @param {boolean} isEnabled - Whether the caller can act on the result; read-only users cannot.
         * @returns {UseQueryOptions<WorkMatchAnalysisResult | null, Error>} Query options.
         */
        getWorkMatchAnalysis: (
            payEquityAuditId: string | null | undefined,
            employmentIds: string[],
            organisationNodeId: string | null | undefined,
            role: Role | null | undefined,
            isEnabled: boolean
        ): UseQueryOptions<WorkMatchAnalysisResult | null, Error> => {
            const hasInput = isEnabled && payEquityAuditId != null && payEquityAuditId !== '' && employmentIds.length > 0 && organisationNodeId != null && organisationNodeId !== '' && role != null;
            const topic = hasInput ? employmentWorkDetailsTopic(payEquityAuditId, organisationNodeId, role) : '';

            return queryOptions<WorkMatchAnalysisResult | null, Error>({
                queryKey: ['payEquityAuditEmployments', 'workMatchAnalysis', topic, employmentIds],
                enabled: () => hasInput,
                queryFn: async (): Promise<WorkMatchAnalysisResult | null> => {
                    if (topic === '' || employmentIds.length === 0) return null;
                    const res = await ResgateManager.getInstance().call<WorkMatchAnalysisResult>(topic, 'work-match-analysis', employmentIds);
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                },
            });
        },
        /**
         * How many employments in the audit have a missing or zero salary.
         * @param {string | null | undefined} payEquityAuditId - The audit.
         * @returns {UseQueryOptions<FieldInfo | null, Error, number>} Query options selecting the count.
         */
        getZeroSalaryCount: (payEquityAuditId: string | null | undefined): UseQueryOptions<FieldInfo | null, Error, number> => {
            const { instance } = useLoginStore();
            const hasInput = payEquityAuditId != null && payEquityAuditId !== '';

            return queryOptions<FieldInfo | null, Error, number>({
                queryKey: ['payEquityAuditEmployments', 'zeroSalaryCount', instance, payEquityAuditId],
                enabled: () => hasInput,
                queryFn: async (): Promise<FieldInfo | null> => {
                    if (payEquityAuditId == null || payEquityAuditId === '') return null;
                    return newApi.analytics.getFieldInfo(InputFieldNames.salary, DataSources.MappingEmployments, zeroSalaryFilter(payEquityAuditId));
                },
                select: (res): number => res?.uniqueValues ?? 0,
            });
        },
        // The three below back AG Grid's server-side row model and the selection round trip. They are
        // reads, but AG Grid owns block caching and the payloads are large and short-lived, so they
        // are meant for `queryClient.fetchQuery(...)` rather than `useQuery`, and keep nothing after
        // the call resolves.
        //
        // `retry: false` matters here: the grid wants to hear about a failed block straight away so
        // it can show its retry row. With the default `retry: 3` a failing block would sit in the
        // loading state for ~7s across four requests before the caller ever sees the rejection.

        /**
         * One page of employments for the grid.
         * @param {string} payEquityAuditId - The audit.
         * @param {EmploymentFilterInput} input - Page, sort, filter and group.
         * @param {string | null} organisationNodeId - The user's organisation node.
         * @param {Role | null} role - The user's active role.
         * @returns {UseQueryOptions<EmploymentData, Error>} Query options.
         */
        getEmployments: (payEquityAuditId: string, input: EmploymentFilterInput, organisationNodeId: string | null, role: Role | null): UseQueryOptions<EmploymentData, Error> => {
            const { instance } = useLoginStore();
            const path = employmentsPath(payEquityAuditId, organisationNodeId, role);

            return queryOptions<EmploymentData, Error>({
                queryKey: ['payEquityAuditEmployments', 'employments', instance, path, input],
                gcTime: 0,
                retry: false,
                queryFn: async (): Promise<EmploymentData> => {
                    return restManager.post<EmploymentData>(input, `v1/${instance}/${path}`, { 'x-subdomain': instance ?? '' });
                },
            });
        },
        /**
         * One page of grouped employments for the grid.
         * @param {string} payEquityAuditId - The audit.
         * @param {EmploymentGroupFilterInput} input - Page, sort, group type and filter.
         * @param {string | null} organisationNodeId - The user's organisation node.
         * @param {Role | null} role - The user's active role.
         * @returns {UseQueryOptions<EmploymentGrouping, Error>} Query options.
         */
        getEmploymentGroups: (payEquityAuditId: string, input: EmploymentGroupFilterInput, organisationNodeId: string | null, role: Role | null): UseQueryOptions<EmploymentGrouping, Error> => {
            const { instance } = useLoginStore();
            const path = employmentGroupsPath(payEquityAuditId, organisationNodeId, role);

            return queryOptions<EmploymentGrouping, Error>({
                queryKey: ['payEquityAuditEmployments', 'employmentGroups', instance, path, input],
                gcTime: 0,
                retry: false,
                queryFn: async (): Promise<EmploymentGrouping> => {
                    return restManager.post<EmploymentGrouping>(input, `v1/${instance}/${path}`, { 'x-subdomain': instance ?? '' });
                },
            });
        },
        /**
         * Resolves an AG Grid server-side selection into employment ids.
         *
         * The grid cannot enumerate a selection that spans unloaded blocks, so the server does it.
         * @param {string} payEquityAuditId - The audit.
         * @param {EmploymentFilterSelectInput} input - Filter plus the grid's selection state.
         * @param {string | null | undefined} loginUserId - The signed-in user.
         * @param {Role | null | undefined} role - The user's active role.
         * @returns {UseQueryOptions<EmploymentDataSelect, Error>} Query options.
         */
        getFilteredEmploymentSelection: (
            payEquityAuditId: string,
            input: EmploymentFilterSelectInput,
            loginUserId: string | null | undefined,
            role: Role | null | undefined
        ): UseQueryOptions<EmploymentDataSelect, Error> => {
            const { instance } = useLoginStore();
            const path = `v1/${instance}/${loginUserId}/${role}/pay-equity-audit/${payEquityAuditId}/filtered-employment-select`;

            return queryOptions<EmploymentDataSelect, Error>({
                queryKey: ['payEquityAuditEmployments', 'filteredEmploymentSelection', instance, path, input],
                gcTime: 0,
                retry: false,
                queryFn: async (): Promise<EmploymentDataSelect> => {
                    try {
                        return await restManager.post<EmploymentDataSelect>(input, path, { 'x-subdomain': instance ?? '' });
                    } catch (error: unknown) {
                        throw new Error('Failed to get filtered employment selection in [filtered-employment-select] endpoint.', { cause: error });
                    }
                },
            });
        },
    },
};
